import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  Wrench, 
  Shield, 
  Camera, 
  Zap, 
  Clock, 
  Star,
  Phone,
  CheckCircle,
  ArrowRight,
  Play,
  ImageIcon,
  FileText,
  Gauge,
  AlertTriangle,
  Users,
  MapPin,
  Droplet
} from 'lucide-react';
import { SchemaInjector, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { BreadcrumbNavigation, createBreadcrumbData } from '../components/BreadcrumbNavigation';

// Responsive Video Component
const VideoEmbed = ({ 
  src, 
  title, 
  width = "560", 
  height = "315",
  description,
  localRelevance 
}: {
  src: string;
  title: string;
  width?: string;
  height?: string;
  description?: string;
  localRelevance?: string;
}) => (
  <div className="video-embed-container my-8">
    <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
      <iframe 
        className="absolute top-0 left-0 w-full h-full rounded-lg shadow-lg"
        src={src}
        title={title}
        frameBorder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
      />
    </div>
    {description && (
      <div className="video-description mt-4 p-4 bg-white/10 backdrop-blur-sm rounded border border-white/20">
        <p className="text-sm text-gray-700">{description}</p>
        {localRelevance && (
          <p className="text-xs text-blue-600 mt-2">
            📍 {localRelevance}
          </p>
        )}
      </div>
    )}
  </div>
);

const SewerRepairReplacement = () => {
  // Schema markup data for Sewer Repair
  const sewerScopeServiceSchema = createServiceSchema(
    'Sewer Camera Inspection Denver',
    'Professional sewer camera scope inspection in Denver Metro. Camera scope $175, spot repair $5,000, full replacement $12,000. Only Colorado plumber showing real costs.',
    175
  );

  const sewerRepairServiceSchema = createServiceSchema(
    'Sewer Line Replacement Denver',
    'Complete sewer line replacement services in Denver Metro. Full replacement $12,000, spot repairs from $5,000. Master plumber owned with transparent pricing.',
    12000
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Services', url: 'https://colorado-plumbing.com/#services' },
    { name: 'Sewer Repair', url: 'https://colorado-plumbing.com/sewer-repair-replacement' }
  ]);

  const navigate = useNavigate();
  
  const handleContactClick = () => {
    navigate('/#contact');
    // Small delay to ensure navigation happens first
    setTimeout(() => {
      const contactElement = document.getElementById('contact');
      if (contactElement) {
        contactElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  useEffect(() => {
    // Meta tags are now handled by static generation in generate-static.cjs
    // This ensures consistency and avoids conflicts between React and static HTML

    // Add Sewer Repair & Replacement Service Schema for AI SEO
    const sewerServiceSchema = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      '@id': 'https://colorado-plumbing.com/sewer-repair-replacement#service',
      'name': 'Sewer Repair & Replacement Services',
      'description': 'Colorado\'s #1 sewer repair and replacement specialists with 300+ permitted projects. Traditional excavation, trenchless pipe bursting, underslab replacement, and emergency sewer services.',
      'provider': {
        '@type': 'LocalBusiness',
        'name': 'Colorado First Plumbing and Sewer LLC',
        'email': 'codee@colorado-plumbing.com',
        'url': 'https://colorado-plumbing.com',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': '5108 Sydney Ave',
          'addressLocality': 'Littleton',
          'addressRegion': 'CO',
          'postalCode': '80130',
          'addressCountry': 'US'
        },
        'aggregateRating': {
          '@type': 'AggregateRating',
          'ratingValue': '4.8',
          'reviewCount': '360',
          'bestRating': '5',
          'worstRating': '1'
        }
      },
      'areaServed': [
        {
          '@type': 'City',
          'name': 'Denver',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Highlands Ranch',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Littleton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lakewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Arvada',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Thornton',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Longmont',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Aurora',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Lone Tree',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Castle Rock',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Englewood',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Centennial',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Westminster',
          'addressRegion': 'CO'
        },
        {
          '@type': 'City',
          'name': 'Loveland',
          'addressRegion': 'CO'
        }
      ],
      'serviceType': 'Sewer and Drain Services',
      'category': 'Plumbing Services',
      'hasOfferCatalog': {
        '@type': 'OfferCatalog',
        'name': 'Sewer Repair & Replacement Services',
        'itemListElement': [
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Traditional Sewer Repair',
              'description': 'Conventional excavation and replacement of damaged sewer lines with full permitting'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Trenchless Sewer Repair',
              'description': 'Modern pipe bursting and trenchless sewer replacement technology'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Underslab Sewer Replacement',
              'description': 'Specialized underslab sewer line replacement for complex situations'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Emergency Sewer Services',
              'description': '24/7 emergency sewer repair and drain cleaning services'
            }
          },
          {
            '@type': 'Offer',
            'itemOffered': {
              '@type': 'Service',
              'name': 'Sewer Camera Inspection',
              'description': 'Advanced camera inspection and locating services for accurate diagnosis'
            }
          }
        ]
      },
      'offers': {
        '@type': 'Offer',
        'priceCurrency': 'USD',
        'availability': 'https://schema.org/InStock',
        'description': 'Professional sewer repair and replacement with 300+ permitted projects and emergency availability'
      },
      'additionalProperty': [
        {
          '@type': 'PropertyValue',
          'name': 'Emergency Service',
          'value': '8AM-5PM Mon-Sat ($200 fee)'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Master Plumber Owned',
          'value': 'Yes'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Permitted Projects',
          'value': '300+ Completed'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Specialization',
          'value': 'Colorado\'s #1 Sewer Specialists'
        },
        {
          '@type': 'PropertyValue',
          'name': 'Technology',
          'value': 'Traditional & Trenchless Methods'
        }
      ]
    };

    // Inject the service schema
    let serviceSchemaScript = document.querySelector('#sewer-service-schema');
    if (serviceSchemaScript) {
      serviceSchemaScript.textContent = JSON.stringify(sewerServiceSchema);
    } else {
      serviceSchemaScript = document.createElement('script');
      serviceSchemaScript.setAttribute('id', 'sewer-service-schema');
      serviceSchemaScript.setAttribute('type', 'application/ld+json');
      serviceSchemaScript.textContent = JSON.stringify(sewerServiceSchema);
      document.head.appendChild(serviceSchemaScript);
    }
  }, []);

  useEffect(() => {
    const loadBirdEyeWidget = () => {
      // Check if script already exists
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      // Create and load the BirdEye script
      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      
      script.onload = () => {
        console.log('BirdEye widget loaded successfully');
      };
      
      script.onerror = () => {
        console.error('Failed to load BirdEye widget');
        // Show fallback content
        const fallback = document.querySelector('#birdeye-fallback') as HTMLElement;
        if (fallback) fallback.style.display = 'block';
      };

      document.head.appendChild(script);
    };

    // Load the BirdEye widget after a short delay to ensure DOM is ready
    const timer = setTimeout(() => {
      loadBirdEyeWidget();
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 to-blue-950 font-inter text-white">
      <SchemaInjector 
        schemas={[sewerScopeServiceSchema, sewerRepairServiceSchema, breadcrumbSchema]}
        title="Sewer Repair Denver | $175 Scope vs $12,000 Replacement | Real Prices"
        description="Denver sewer specialists showing actual prices online. Camera scope $175, spot repair $5,000, full replacement $12,000. 360+ reviews. Master plumber owned."
        canonical="https://colorado-plumbing.com/sewer-repair-replacement"
        keywords="sewer repair cost Denver, sewer scope $175, sewer replacement $12000, transparent sewer pricing, actual sewer costs online"
      />
      <Header />
      <BreadcrumbNavigation 
        {...createBreadcrumbData.service('Sewer Repair & Replacement')}
      />
      <main>
        {/* Hero Section */}
        <section className="pt-56 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary text-white relative overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] bg-repeat"></div>
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center pt-8 pb-8">
              <div className="flex items-center justify-center mb-6">
                <Wrench className="mr-3 text-action-warm" size={40} />
                <span className="text-sm font-medium bg-white/20 backdrop-blur-sm text-white px-3 py-1 rounded-full border border-white/30">
                  Colorado's #1 Sewer Specialists
                </span>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold mb-6 text-neutral-white">
                Denver's Expert Sewer Repair & Replacement Specialists
              </h1>

              <p className="text-xl mb-8 text-trust-light/90">
                <strong>300+ permitted sewer replacement projects</strong> completed with traditional excavation and trenchless methods. 
                Expert <Link to="/residential-plumbing" className="text-action-warm hover:text-white underline">residential</Link> and 
                <Link to="/commercial-plumbing" className="text-action-warm hover:text-white underline"> commercial sewer services</Link> across all 
                <Link to="/denver-plumber" className="text-action-warm hover:text-white underline"> Denver Metro</Link> areas. 
                <Link to="/sewer-replacement-spot-repair-pricing" className="text-action-warm hover:text-white underline">See our actual sewer replacement pricing</Link> with complete permit handling.
              </p>
              
              <div className="mb-8 bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 max-w-2xl mx-auto">
                <div id="bf-revz-widget-9876543218815183" aria-label="Customer Reviews Widget"></div>
                <div id="birdeye-fallback" style={{ display: 'none' }} className="text-trust-light">
                  <p>Reviews loading... See our 310+ verified sewer project reviews on Google and BirdEye.</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                
                <button 
                  onClick={handleContactClick}
                  className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-8 py-4 rounded-full text-xl font-semibold inline-block transition-all duration-300"
                >
                  Get Free Sewer Assessment
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Colorado-Specific Sewer Expertise Section */}
        <section className="py-16 md:py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Colorado Sewer Line Expertise</h2>
              <p className="text-lg text-blue-100">
                Colorado's unique environment presents specific challenges that require specialized knowledge for sewer systems. 
                Our team understands these complexities and provides expert solutions tailored to Front Range conditions.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {/* Expansive Soils */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-orange-100 w-fit mx-auto mb-4">
                  <AlertTriangle className="text-orange-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Expansive Soil Movement</h3>
                <p className="text-sm text-blue-100">
                  Colorado's clay soils constantly shift, causing sewer bellies, pipe breaks, and joint failures. Specialized knowledge prevents recurring problems.
                </p>
              </div>

              {/* Aging Infrastructure */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-red-100 w-fit mx-auto mb-4">
                  <Clock className="text-red-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Aging Pipe Materials</h3>
                <p className="text-sm text-blue-100">
                  Many Denver Metro homes have clay and cast iron pipes from the 1970s. Understanding material failures saves costly surprises.
                </p>
              </div>

              {/* Tree Root Challenges */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-green-100 w-fit mx-auto mb-4">
                  <Shield className="text-green-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Root Intrusion Prevention</h3>
                <p className="text-sm text-blue-100">
                  Beautiful mature trees create sewer line challenges. Expert knowledge of root patterns and modern pipe materials provides solutions.
                </p>
              </div>

              {/* Temperature Extremes */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg text-center border border-white/20">
                <div className="p-3 rounded-lg bg-blue-100 w-fit mx-auto mb-4">
                  <Gauge className="text-blue-600" size={28} />
                </div>
                <h3 className="text-lg font-bold mb-3 text-white">Freeze-Thaw Cycles</h3>
                <p className="text-sm text-blue-100">
                  Extreme temperature changes affect pipe integrity and joint stability. Proper installation prevents seasonal failures.
                </p>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg max-w-4xl mx-auto border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white text-center">Why Colorado Expertise Matters for Sewer Lines</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold mb-2 text-trust-primary">Environmental Challenges</h4>
                  <ul className="space-y-1 text-sm text-blue-100">
                    <li>• Expansive clay soils causing pipe movement and bellies</li>
                    <li>• Freeze-thaw cycles affecting joint integrity</li>
                    <li>• Mature tree root systems seeking moisture sources</li>
                    <li>• Aging infrastructure in established neighborhoods</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold mb-2 text-trust-primary">Our Specialized Solutions</h4>
                  <ul className="space-y-1 text-sm text-blue-100">
                    <li>• Advanced camera inspection and precise location services</li>
                    <li>• Root barrier installation and prevention strategies</li>
                    <li>• Modern pipe materials resistant to ground movement</li>
                    <li>• Proper slope correction for long-term performance</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Homeowner Challenges & Solutions Section */}
        <section className="py-16 md:py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Understanding Homeowner Sewer Responsibilities</h2>
              <p className="text-lg text-blue-100">
                Many homeowners are surprised to learn the extent of their sewer line responsibilities. We help you understand 
                what you're responsible for and provide proactive solutions to protect your investment.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* First-Time Homeowners */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 shadow-lg border border-blue-100">
                <div className="p-3 rounded-lg bg-blue-100 w-fit mb-6">
                  <FileText className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">First-Time Homeowners</h3>
                <p className="text-blue-100 mb-6">
                  Understanding your sewer line responsibility from your foundation to the city main connection, 
                  even portions under public streets and sidewalks.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-trust-primary">Common Misconceptions We Address:</h4>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• "The city maintains sewer lines under streets"</li>
                    <li>• "Homeowner's insurance covers sewer repairs"</li>
                    <li>• "Sewer problems are obvious when they occur"</li>
                    <li>• "All plumbers understand Colorado soil challenges"</li>
                  </ul>
                </div>

                <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                  <h4 className="font-semibold mb-2 text-blue-800">Our Educational Approach:</h4>
                  <ul className="space-y-1 text-sm text-blue-700">
                    <li>• Clear explanation of responsibility boundaries</li>
                    <li>• Proactive maintenance recommendations</li>
                    <li>• <Link to="/sewer-replacement-spot-repair-pricing" className="underline hover:text-blue-900">Transparent pricing</Link> for all services</li>
                    <li>• Video documentation of all findings</li>
                  </ul>
                </div>
              </div>

              {/* Older Home Owners */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-8 shadow-lg border border-orange-100">
                <div className="p-3 rounded-lg bg-orange-100 w-fit mb-6">
                  <Clock className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Older Home Owners</h3>
                <p className="text-blue-100 mb-6">
                  Homes built before 1970 often have clay or cast iron sewer lines requiring specialized knowledge 
                  and proactive maintenance strategies.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-trust-primary">Aging Infrastructure Challenges:</h4>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Clay pipes with multiple joint entry points for roots</li>
                    <li>• Cast iron corrosion from hydrogen sulfide exposure</li>
                    <li>• Orangeburg pipe deterioration and collapse risk</li>
                    <li>• Sewer bellies from decades of soil settlement</li>
                  </ul>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-orange-800">Our Specialized Solutions:</h4>
                  <ul className="space-y-1 text-sm text-orange-700">
                    <li>• Regular camera inspections to catch issues early</li>
                    <li>• Modern pipe replacement with 50+ year lifespan</li>
                    <li>• Root barrier installation during replacement</li>
                    <li>• Complete permit handling and documentation</li>
                  </ul>
                </div>
              </div>

              {/* Property Buyers */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 shadow-lg border border-green-100">
                <div className="p-3 rounded-lg bg-green-100 w-fit mb-6">
                  <Camera className="text-green-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Property Buyers</h3>
                <p className="text-blue-100 mb-6">
                  Sewer scope inspections are now standard in Denver Metro real estate transactions. 
                  Protect your investment with professional sewer line assessment.
                </p>
                
                <div className="mb-6">
                  <h4 className="font-semibold mb-3 text-trust-primary">Critical Inspection Points:</h4>
                  <ul className="space-y-2 text-sm text-blue-100">
                    <li>• Complete camera inspection from house to city main</li>
                    <li>• Identification of pipe material and condition</li>
                    <li>• Assessment of root intrusion and blockages</li>
                    <li>• Documentation of any structural defects</li>
                  </ul>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-green-800">Purchase Protection Benefits:</h4>
                  <ul className="space-y-1 text-sm text-green-700">
                    <li>• Negotiation power with sellers for needed repairs</li>
                    <li>• Accurate cost estimates for future maintenance</li>
                    <li>• Peace of mind with complete system knowledge</li>
                    <li>• Video documentation for insurance purposes</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Sewer Issues & Expert Solutions */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Common Sewer Issues & Our Expert Solutions</h2>
              <p className="text-lg text-blue-100">
                Understanding the most common sewer problems in Denver Metro helps you recognize early warning signs 
                and make informed decisions about repairs. Our expertise ensures lasting solutions.
              </p>
            </div>

            <div className="space-y-8">
              {/* Sewer Belly Problem-Solution */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-orange-100 mr-3">
                        <AlertTriangle className="text-orange-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Sewer Belly (Sagging Pipes)</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>The Problem:</strong> Colorado's expansive clay soils cause pipes to sag, creating low points where waste collects instead of flowing smoothly.
                    </p>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Warning Signs:</strong> Frequent clogs, slow drains, sewage backups, gurgling sounds.
                    </p>

                    {/* Sewer Problem Examples */}
                    <div className="mt-4">
                      <img 
                        src="/colorado-plumbing-uploads/locations/westminster/sewer-bedding-westminster.JPEG" 
                        alt="Colorado First Plumbing showing proper sewer pipe bedding technique in Westminster to prevent future bellies"
                        className="w-full h-48 object-cover rounded-lg shadow-md mb-2"
                      />
                      <p className="text-xs text-gray-600">Our proper bedding technique prevents future sewer bellies - Westminster project</p>
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-trust-primary">Our Comprehensive Solution Approach:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• <Link to="/diagnostic-services-pricing" className="text-orange-600 hover:text-orange-800 underline">Advanced camera inspection</Link> to measure exact sag severity</li>
                        <li>• Proper slope correction through traditional excavation</li>
                        <li>• Modern pipe materials resistant to ground movement</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• Soil stabilization recommendations</li>
                        <li>• Complete permit handling and inspections</li>
                        <li>• <Link to="/sewer-replacement-spot-repair-pricing" className="text-orange-600 hover:text-orange-800 underline">Transparent pricing for all repair options</Link></li>
                        <li>• <Link to="/hydro-jetting-pricing" className="text-orange-600 hover:text-orange-800 underline">Hydro jetting for belly cleaning</Link></li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Root Intrusion Problem-Solution */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-green-100 mr-3">
                        <Shield className="text-green-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Tree Root Intrusion</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>The Problem:</strong> Mature trees seek moisture from sewer lines, with roots entering through joints and growing to cause blockages and pipe damage.
                    </p>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>High Risk:</strong> Clay pipes with multiple joints, especially in tree-lined neighborhoods.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-trust-primary">Our Root Management Strategy:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• <Link to="/root-removal-pricing" className="text-green-600 hover:text-green-800 underline">Professional root cutting</Link> and <Link to="/hydro-jetting-pricing" className="text-green-600 hover:text-green-800 underline">hydro jetting</Link></li>
                        <li>• Modern PVC/SDR pipe replacement with sealed joints</li>
                        <li>• Root barrier installation during replacement</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• <Link to="/sewer-snake-auger-pricing" className="text-green-600 hover:text-green-800 underline">Regular maintenance programs</Link> for prevention</li>
                        <li>• <Link to="/sewer-cleaning-pricing" className="text-green-600 hover:text-green-800 underline">Complete sewer cleaning services</Link></li>
                        <li>• Tree root mapping and management planning</li>
                        <li>• Long-term solutions that eliminate recurring problems</li>
                      </ul>
                    </div>
                    
                    {/* Root Removal Video */}
                    <div className="mt-6">
                      <h4 className="font-semibold mb-3 text-green-800">Watch: Professional Root Removal Process</h4>
                      <VideoEmbed 
                        src="https://www.youtube.com/embed/usssWbIeNCQ"
                        title="Removing Roots in a Sewer Pipe"
                        description="See our professional root removal techniques that eliminate recurring blockages and restore proper flow."
                        localRelevance="Demonstration of root cutting equipment used throughout Colorado Front Range"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Cast Iron Corrosion Problem-Solution */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-red-100 mr-3">
                        <Clock className="text-red-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Cast Iron Pipe Corrosion</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>The Problem:</strong> Homes built before 1970 often have cast iron pipes that corrode from hydrogen sulfide exposure and Denver's moist clay soils.
                    </p>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Silent Danger:</strong> Corrosion happens gradually, often undetected until catastrophic failure.
                    </p>

                    {/* Cast Iron Examples */}
                    <div className="mt-4">
                      <img 
                        src="/colorado-plumbing-uploads/locations/littleton/sewer-street-replacement-littleton.jpeg" 
                        alt="Colorado First Plumbing team performing street excavation for cast iron sewer replacement in Littleton"
                        className="w-full h-48 object-cover rounded-lg shadow-md mb-2"
                      />
                      <p className="text-xs text-gray-600">Professional street excavation for cast iron replacement - Littleton project</p>
                    </div>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-trust-primary">Our Proactive Replacement Strategy:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• Comprehensive camera inspection to assess corrosion extent</li>
                        <li>• Complete pipe replacement with modern materials</li>
                        <li>• Proper documentation for insurance claims</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• 50+ year lifespan with modern pipe materials</li>
                        <li>• Complete permit handling and final inspections</li>
                        <li>• <Link to="/sewer-cleaning-pricing" className="text-red-600 hover:text-red-800 underline">Preventive cleaning services</Link></li>
                        <li>• <Link to="/water-heater-pricing" className="text-red-600 hover:text-red-800">Water heater coordination if needed</Link></li>
                      </ul>
                    </div>

                    {/* Specialized Equipment */}
                    <div className="bg-red-50 rounded-lg p-4">
                      <h4 className="font-semibold mb-3 text-red-800">Specialized Cast Iron Solutions:</h4>
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <img 
                          src="/colorado-plumbing-uploads/existing-projects/sewer-repair-experts.png" 
                          alt="Cast iron to PVC replacement by sewer repair experts"
                          className="w-full h-32 object-cover rounded"
                        />
                        <img 
                          src="/colorado-plumbing-uploads/services/sewer-services/descale-machine-roots-denver.JPEG" 
                          alt="Professional descaling machine for cast iron cleaning in Denver"
                          className="w-full h-32 object-cover rounded"
                        />
                      </div>
                      <ul className="space-y-1 text-sm text-red-700">
                        <li>• <Link to="/cast-iron-descaling-pricing" className="text-red-600 hover:text-red-800 underline">Professional descaling and assessment</Link></li>
                        <li>• Complete repiping with modern PVC materials</li>
                        <li>• <Link to="/sewer-replacement-spot-repair-pricing" className="text-red-600 hover:text-red-800 underline">Transparent replacement pricing</Link></li>
                        <li>• Minimal disruption to your property</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              {/* Pipe Breaks & Structural Failures Problem-Solution */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg border border-white/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div>
                    <div className="flex items-center mb-4">
                      <div className="p-2 rounded-lg bg-blue-100 mr-3">
                        <Wrench className="text-blue-600" size={24} />
                      </div>
                      <h3 className="text-xl font-bold text-white">Pipe Breaks & Structural Failures</h3>
                    </div>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>The Problem:</strong> Multiple causes including freeze-thaw cycles, ground shifting, heavy vehicle traffic, and construction activity can cause sudden pipe failures.
                    </p>
                    <p className="text-sm text-blue-100 mb-3">
                      <strong>Emergency Signs:</strong> Sewage backup, wet spots in yard, strong sewage odors, foundation settling.
                    </p>
                  </div>
                  <div className="lg:col-span-2">
                    <h4 className="font-semibold mb-3 text-trust-primary">Our Emergency Response & Repair Process:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• Immediate camera inspection to pinpoint exact break location</li>
                        <li>• Assessment of appropriate repair method (spot repair vs replacement)</li>
                        <li>• Soil analysis and stabilization recommendations</li>
                      </ul>
                      <ul className="space-y-2 text-sm text-blue-100">
                        <li>• Emergency weekend/evening response when available</li>
                        <li>• Coordination with utility location services</li>
                        <li>• Complete restoration and cleanup included</li>
                      </ul>
                    </div>

                    {/* Spot Repair Process Videos */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                      <div>
                        <h4 className="font-semibold mb-3 text-blue-800">Spot Repair Technique</h4>
                        <VideoEmbed 
                          src="https://www.youtube.com/embed/WzQdOtVNeUU"
                          title="Sewer Spot Repair 7-24-2024"
                          description="Professional spot repair technique showing precise excavation and pipe replacement methods."
                        />
                      </div>
                      <div>
                        <h4 className="font-semibold mb-3 text-blue-800">Safety & Shoring</h4>
                        <VideoEmbed 
                          src="https://www.youtube.com/embed/S2NZP_g5t_Y"
                          title="Sewer Repair with Hydraulic Shoring - Safety"
                          description="Proper safety procedures and hydraulic shoring for deep excavation sewer repairs."
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Advanced Technology & Methods Section */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Advanced Sewer Repair Technology</h2>
              <p className="text-lg text-blue-100">
                We use the latest technology and proven methods to provide accurate diagnosis and lasting repairs. 
                See our equipment and processes in action.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-16">
              {/* Traditional & Trenchless Methods */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-white">Traditional & Trenchless Methods</h3>
                
                {/* Pipe Bursting Video */}
                <div className="mb-8">
                  <h4 className="font-semibold mb-3 text-trust-primary">Trenchless Pipe Bursting</h4>
                  <VideoEmbed 
                    src="https://www.youtube.com/embed/6zwWVAprXIs"
                    title="1580 Pipe Bursting"
                    description="Advanced trenchless pipe bursting technology for minimal disruption sewer replacement."
                    localRelevance="Used throughout Denver Metro for preserving landscaping and driveways"
                  />
                </div>

                {/* Another Spot Repair Video */}
                <div>
                  <h4 className="font-semibold mb-3 text-trust-primary">Precision Spot Repairs</h4>
                  <VideoEmbed 
                    src="https://www.youtube.com/embed/OF9PXwFpj2I"
                    title="Sewer line spot Repair 7-15-24"
                    description="Precise spot repair technique minimizing excavation while ensuring permanent solutions."
                  />
                </div>
              </div>

              {/* Camera Technology */}
              <div>
                <h3 className="text-2xl font-bold mb-6 text-white">Professional Camera Inspection</h3>
                
                {/* Camera Equipment Photos */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div>
                    <img 
                      src="/colorado-plumbing-uploads/services/sewer-services/camera-scope-sewer-arvada.JPEG" 
                      alt="Professional sewer camera scope equipment in Arvada"
                      className="w-full h-40 object-cover rounded-lg shadow-md"
                    />
                    <p className="text-sm text-gray-600 mt-2">HD Camera Scope Equipment</p>
                  </div>
                  <div>
                    <img 
                      src="/colorado-plumbing-uploads/locations/highlands-ranch/sewer-camera-locate-highlands-ranch.JPEG" 
                      alt="Sewer camera locating service in Highlands Ranch"
                      className="w-full h-40 object-cover rounded-lg shadow-md"
                    />
                    <p className="text-sm text-gray-600 mt-2">Precise GPS Location Services</p>
                  </div>
                </div>

                <div className="bg-white/10 rounded-lg p-6 border border-white/20">
                  <h4 className="font-semibold mb-3 text-blue-800">Our Camera Inspection Advantages:</h4>
                  <ul className="space-y-2 text-sm text-blue-700">
                    <li>• <Link to="/diagnostic-services-pricing" className="text-blue-600 hover:text-blue-800 underline">HD video with exact depth and location measurements</Link></li>
                    <li>• GPS coordinates for precise excavation planning</li>
                    <li>• Complete video documentation for insurance</li>
                    <li>• Before and after verification of all repairs</li>
                    <li>• Digital copies provided to homeowners</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Equipment Gallery */}
            <div className="bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-8">
              <h3 className="text-2xl font-bold mb-6 text-white text-center">Professional Equipment & Results</h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/excavation-replace-sewer-expert.JPEG" 
                    alt="Expert sewer excavation and replacement"
                    className="w-full h-40 object-cover rounded-lg shadow-md mb-2"
                  />
                  <p className="text-xs text-gray-600">Expert Excavation</p>
                </div>
                <div className="text-center">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-repair-double-clean-out-thornton.JPEG" 
                    alt="Professional double cleanout installation in Thornton sewer repair"
                    className="w-full h-40 object-cover rounded-lg shadow-md mb-2"
                  />
                  <p className="text-xs text-gray-600">Double Cleanout Installation</p>
                </div>
                <div className="text-center">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/double-cleanout-pvc.JPEG" 
                    alt="Professional PVC double cleanout installation"
                    className="w-full h-40 object-cover rounded-lg shadow-md mb-2"
                  />
                  <p className="text-xs text-gray-600">Modern PVC Materials</p>
                </div>
                <div className="text-center">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-camera-verify-repair-littleton.JPEG" 
                    alt="Camera verification of completed sewer repair in Littleton"
                    className="w-full h-40 object-cover rounded-lg shadow-md mb-2"
                  />
                  <p className="text-xs text-gray-600">Repair Verification</p>
                </div>
              </div>

              <div className="text-center">
                <p className="text-sm text-gray-600 mb-4">
                  Every project includes complete documentation, from initial diagnosis through final verification
                </p>
                <Link 
                  to="/sewer-replacement-spot-repair-pricing" 
                  className="bg-trust-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-trust-primary/90 transition-colors inline-flex items-center"
                >
                  <Camera className="mr-2" size={20} />
                  See Our Sewer Repair Pricing
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Enhanced Competitive Advantages */}
        <section className="py-20 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Why Choose Colorado First Plumbing for Sewer Services?</h2>
              <p className="text-lg text-blue-100">
                Our specialized expertise in Colorado's unique sewer challenges, combined with transparent pricing 
                and proven results, makes us the trusted choice for homeowners throughout Denver Metro.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* 300+ Permitted Sewer Projects */}
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-8 shadow-lg border border-blue-100">
                <div className="p-3 rounded-lg bg-blue-100 w-fit mb-6">
                  <FileText className="text-blue-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">300+ Permitted Sewer Projects</h3>
                <p className="text-blue-100 mb-6">
                  Extensive experience with both traditional excavation and trenchless replacement methods across 
                  Denver Metro, with complete permit handling and documentation for every project.
                </p>
                
                {/* Project Documentation Examples */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/flow-fill-street-sewer-repair-denver.JPEG" 
                    alt="Professional flow fill street sewer repair in Denver with proper restoration"
                    className="w-full h-32 object-cover rounded-lg shadow-md"
                  />
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/traffic-rated-double-cleanout-sewer-denver.JPEG" 
                    alt="Traffic rated double cleanout installation for sewer access in Denver"
                    className="w-full h-32 object-cover rounded-lg shadow-md"
                  />
                </div>
                
                <div className="bg-white/10 rounded-lg p-4 border border-white/20">
                  <h4 className="font-semibold mb-2 text-blue-800">Our Track Record:</h4>
                  <ul className="space-y-1 text-sm text-blue-700">
                    <li>• 300+ completed sewer repairs and replacements</li>
                    <li>• 100% permit approval rate with local jurisdictions</li>
                    <li>• Complete documentation for insurance claims</li>
                    <li>• Video proof of all work performed</li>
                  </ul>
                </div>
              </div>

              {/* Colorado Soil Expertise */}
              <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-8 shadow-lg border border-orange-100">
                <div className="p-3 rounded-lg bg-orange-100 w-fit mb-6">
                  <AlertTriangle className="text-orange-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Colorado Soil Expertise</h3>
                <p className="text-blue-100 mb-6">
                  Deep understanding of how Denver's expansive clay soils affect sewer lines, with proven solutions 
                  for proper slope correction and long-term stability.
                </p>
                
                <div className="bg-orange-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-orange-800">Specialized Knowledge:</h4>
                  <ul className="space-y-1 text-sm text-orange-700">
                    <li>• Expansive clay soil behavior and pipe settlement patterns</li>
                    <li>• Proper backfill materials and compaction techniques</li>
                    <li>• Slope correction methods for permanent solutions</li>
                    <li>• Soil stabilization recommendations</li>
                  </ul>
                </div>
              </div>

              {/* Advanced Diagnostic Technology */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-8 shadow-lg border border-green-100">
                <div className="p-3 rounded-lg bg-green-100 w-fit mb-6">
                  <Camera className="text-green-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Advanced Diagnostic Technology</h3>
                <p className="text-blue-100 mb-6">
                  State-of-the-art camera inspection equipment provides precise location data and complete 
                  video documentation of all findings for accurate diagnosis and repair planning.
                </p>
                
                {/* Technology Photos */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-camera-littleton.PNG" 
                    alt="Professional sewer camera inspection in Littleton"
                    className="w-full h-32 object-cover rounded-lg shadow-md"
                  />
                  <img 
                    src="/colorado-plumbing-uploads/services/sewer-services/sewer-snake-highlands-ranch-cost.JPEG" 
                    alt="Professional sewer snake equipment in Highlands Ranch"
                    className="w-full h-32 object-cover rounded-lg shadow-md"
                  />
                </div>
                
                <div className="bg-green-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-green-800">Technology Advantages:</h4>
                  <ul className="space-y-1 text-sm text-green-700">
                    <li>• HD camera inspections with precise GPS location</li>
                    <li>• Complete video documentation for insurance</li>
                    <li>• Digital measurements and depth calculations</li>
                    <li>• Before and after video proof of repairs</li>
                  </ul>
                </div>
              </div>

              {/* Homeowner Education Focus */}
              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-8 shadow-lg border border-purple-100">
                <div className="p-3 rounded-lg bg-purple-100 w-fit mb-6">
                  <Users className="text-purple-600" size={32} />
                </div>
                <h3 className="text-2xl font-bold mb-4 text-white">Homeowner Education Focus</h3>
                <p className="text-blue-100 mb-6">
                  We believe in educating homeowners about their sewer responsibilities and providing proactive 
                  maintenance recommendations rather than just reactive emergency repairs.
                </p>
                
                <div className="bg-purple-50 rounded-lg p-4">
                  <h4 className="font-semibold mb-2 text-purple-800">Educational Approach:</h4>
                  <ul className="space-y-1 text-sm text-purple-700">
                    <li>• Clear explanation of homeowner vs city responsibilities</li>
                    <li>• Proactive maintenance recommendations</li>
                    <li>• <Link to="/sewer-replacement-spot-repair-pricing" className="underline hover:text-purple-900">Transparent pricing</Link> with no hidden fees</li>
                    <li>• Preventative solutions over emergency reactions</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Serving All Denver Metro Areas Section */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Serving All Denver Metro Areas</h2>
              <p className="text-lg text-blue-100">
                Professional sewer repair and replacement services across the entire Front Range, with local expertise 
                in each community's specific soil conditions and infrastructure challenges.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              {/* North Metro */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                <div className="flex items-center mb-4">
                  <MapPin className="text-blue-600 mr-3" size={24} />
                  <h3 className="text-xl font-bold text-white">North Metro</h3>
                </div>
                <div className="space-y-3">
                  <Link to="/arvada-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Arvada Sewer Services
                  </Link>
                  <Link to="/thornton-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Thornton Sewer Services  
                  </Link>
                  <Link to="/longmont-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Longmont Sewer Services
                  </Link>
                </div>
                <div className="mt-4 p-3 bg-white/10 rounded border border-white/20">
                  <p className="text-sm text-blue-700">
                    Specializing in clay soil challenges and mature tree root management in established neighborhoods.
                  </p>
                </div>
              </div>

              {/* Central Metro */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                <div className="flex items-center mb-4">
                  <MapPin className="text-green-600 mr-3" size={24} />
                  <h3 className="text-xl font-bold text-white">Central Metro</h3>
                </div>
                <div className="space-y-3">
                  <Link to="/denver-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Denver Sewer Services
                  </Link>
                  <Link to="/lakewood-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Lakewood Sewer Services
                  </Link>
                  <Link to="/littleton-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Littleton Sewer Services
                  </Link>
                </div>
                <div className="mt-4 p-3 bg-green-50 rounded">
                  <p className="text-sm text-green-700">
                    Expert handling of aging infrastructure and cast iron pipe replacement in historic areas.
                  </p>
                </div>
              </div>

              {/* South Metro */}
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-lg border border-white/20">
                <div className="flex items-center mb-4">
                  <MapPin className="text-orange-600 mr-3" size={24} />
                  <h3 className="text-xl font-bold text-white">South Metro</h3>
                </div>
                <div className="space-y-3">
                  <Link to="/highlands-ranch-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Highlands Ranch Sewer Services
                  </Link>
                  <Link to="/centennial-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Centennial Sewer Services
                  </Link>
                  <Link to="/castle-rock-plumber" className="block text-trust-primary hover:text-blue-800 underline">
                    🔧 Castle Rock Sewer Services
                  </Link>
                </div>
                <div className="mt-4 p-3 bg-orange-50 rounded">
                  <p className="text-sm text-orange-700">
                    Specialized solutions for newer construction with modern materials and advanced drainage systems.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg text-center border border-white/20">
              <h3 className="text-2xl font-bold mb-4 text-white">Local Expertise, Consistent Quality</h3>
              <p className="text-blue-100 mb-6">
                While soil conditions and infrastructure vary across the Front Range, our commitment to quality, 
                transparent pricing, and professional service remains consistent in every community we serve.
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <div className="text-2xl font-bold text-trust-primary mb-2">300+</div>
                  <div className="text-sm text-blue-100">Metro-wide Projects</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-trust-primary mb-2">9</div>
                  <div className="text-sm text-blue-100">Service Areas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-trust-primary mb-2">4.9★</div>
                  <div className="text-sm text-blue-100">Average Rating</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-trust-primary mb-2">100%</div>
                  <div className="text-sm text-blue-100">Same Pricing</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Transparent Pricing CTA Section */}
        <section className="py-20 bg-gradient-to-br from-trust-primary to-action-primary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">See Actual Sewer Repair Pricing Online</h2>
              <p className="text-xl mb-8 text-white/90">
                We're the only plumbing company in Colorado showing real sewer repair and replacement pricing online. 
                No estimates, no surprises - just honest, transparent pricing.
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Camera className="text-action-warm mr-3" size={24} />
                    <h3 className="text-lg font-semibold">Sewer Camera Inspection</h3>
                  </div>
                  <div className="mb-4">
                    <img 
                      src="/colorado-plumbing-uploads/services/sewer-services/sewer-inspection-camera-denver.JPEG" 
                      alt="Professional sewer camera inspection equipment in Denver"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-white/80">Complete HD video documentation of your sewer line condition with precise GPS location</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Wrench className="text-action-warm mr-3" size={24} />
                    <h3 className="text-lg font-semibold">Spot Repairs</h3>
                  </div>
                  <div className="mb-4">
                    <img 
                      src="/colorado-plumbing-uploads/services/sewer-services/sewer-spot-repair.JPEG" 
                      alt="Professional spot repair of sewer line"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-white/80">Targeted repairs for specific problem areas using advanced excavation techniques</p>
                </div>
                <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
                  <div className="flex items-center mb-4">
                    <Shield className="text-action-warm mr-3" size={24} />
                    <h3 className="text-lg font-semibold">Complete Replacement</h3>
                  </div>
                  <div className="mb-4">
                    <img 
                      src="/colorado-plumbing-uploads/services/sewer-services/sdr-35-replace-sewer.JPEG" 
                      alt="Modern SDR-35 sewer pipe replacement"
                      className="w-full h-48 object-cover rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-white/80">Full sewer line replacement with modern SDR-35 PVC materials for 50+ year lifespan</p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link 
                  to="/sewer-replacement-spot-repair-pricing" 
                  className="bg-action-warm text-white px-8 py-4 rounded-lg font-semibold hover:bg-action-warm/90 transition-colors inline-flex items-center justify-center"
                >
                  <Star className="mr-2" size={20} />
                  View Sewer Repair Pricing
                </Link>
                <Link 
                  to="/sewer-cleaning-pricing" 
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/30 inline-flex items-center justify-center"
                >
                  <Droplet className="mr-2" size={20} />
                  Sewer Cleaning Pricing
                </Link>
                <Link 
                  to="/hydro-jetting-pricing" 
                  className="bg-white/10 backdrop-blur-sm text-white px-8 py-4 rounded-lg font-semibold hover:bg-white/20 transition-colors border border-white/30 inline-flex items-center justify-center"
                >
                  <Zap className="mr-2" size={20} />
                  Hydro Jetting Pricing
                </Link>
              </div>

              <div className="mt-8 text-sm text-white/70">
                ✓ No Hidden Fees ✓ No Salesmen ✓ No Surprise Charges ✓ Complete Transparency
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 text-white overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">Ready to Solve Your Sewer Problems?</h2>
              <p className="text-lg text-blue-100 mb-8">
                Don't wait for a sewer emergency. Get a professional camera inspection and understand your sewer line condition. 
                We provide honest assessments with transparent pricing and no pressure tactics.
              </p>
              
              <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
                
                <Link 
                  to="/sewer-replacement-spot-repair-pricing" 
                  className="bg-transparent text-trust-primary px-8 py-4 rounded-lg font-semibold hover:bg-trust-primary/5 transition-colors border-2 border-trust-primary inline-flex items-center justify-center"
                >
                  <Star className="mr-2" size={20} />
                  See Repair Pricing
                </Link>
                <Link 
                  to="/sewer-cleaning-pricing" 
                  className="bg-transparent text-trust-primary px-8 py-4 rounded-lg font-semibold hover:bg-trust-primary/5 transition-colors border-2 border-trust-primary inline-flex items-center justify-center"
                >
                  <Droplet className="mr-2" size={20} />
                  See Cleaning Pricing
                </Link>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-trust-primary">300+</div>
                  <div className="text-sm text-blue-100">Sewer Projects</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-trust-primary">4.9★</div>
                  <div className="text-sm text-blue-100">Average Rating</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-trust-primary">100%</div>
                  <div className="text-sm text-blue-100">Permit Approval</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Note: This file is getting long, so I'll add sections incrementally */}
      </main>
      
      <Footer />
    </div>
  );
};

export default SewerRepairReplacement;